import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// cn function for Tailwind class merging (used by shadcn components)
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Utility functions shared across components

/**
 * Format date to locale string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-VE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    });
  } catch {
    return dateString;
  }
};

/**
 * Format date with time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('es-VE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

/**
 * Format currency
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('es-VE', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Format weight
 */
export const formatWeight = (weight, unit = 'lb') => {
  if (!weight) return '-';
  return `${parseFloat(weight).toFixed(2)} ${unit}`;
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Get initials from name
 */
export const getInitials = (name) => {
  if (!name) return '??';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

/**
 * Status label mappings
 */
export const STATUS_LABELS = {
  // Tracking statuses
  pending: 'Pendiente',
  received: 'Recibido en almacén',
  in_transit: 'En tránsito',
  delivered: 'Entregado',
  in_repack: 'En reempaque',
  
  // Repack statuses
  requested: 'Solicitado',
  approved: 'Aprobado',
  processing: 'En proceso',
  completed: 'Completado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
  
  // Payment statuses
  verified: 'Verificado',
  
  // Invoice statuses
  paid: 'Pagada',
  partial: 'Pago parcial',
  
  // Alert statuses
  processed: 'Procesada'
};

/**
 * Get status label
 */
export const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status;
};

/**
 * Shipping method labels
 */
export const SHIPPING_METHODS = {
  air: 'Aéreo',
  sea: 'Marítimo',
  express: 'Express'
};

/**
 * Get shipping method label
 */
export const getShippingMethodLabel = (method) => {
  return SHIPPING_METHODS[method] || method;
};

/**
 * Customer type labels
 */
export const CUSTOMER_TYPES = {
  publico: 'Público',
  alumno: 'Alumno',
  vip: 'VIP'
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone format (Venezuelan)
 */
export const isValidPhone = (phone) => {
  // Accepts formats: 04XX-XXXXXXX, 0412XXXXXXX, +584XXXXXXXXX
  const phoneRegex = /^(\+?58)?0?(412|414|416|424|426)\d{7}$/;
  return phoneRegex.test(phone.replace(/[\s-]/g, ''));
};

/**
 * Generate random ID
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Sort array by key
 */
export const sortByKey = (array, key, order = 'asc') => {
  return [...array].sort((a, b) => {
    let valA = a[key];
    let valB = b[key];
    
    // Handle dates
    if (key.includes('date') || key.includes('_at')) {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }
    
    // Handle strings
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    
    if (order === 'asc') {
      return valA > valB ? 1 : -1;
    }
    return valA < valB ? 1 : -1;
  });
};

/**
 * Filter array by search term across multiple fields
 */
export const filterBySearch = (array, searchTerm, fields) => {
  if (!searchTerm) return array;
  const term = searchTerm.toLowerCase();
  return array.filter(item => 
    fields.some(field => {
      const value = item[field];
      return value && value.toString().toLowerCase().includes(term);
    })
  );
};

/**
 * Group array by key
 */
export const groupByKey = (array, key) => {
  return array.reduce((groups, item) => {
    const group = item[key] || 'Sin categoría';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(item);
    return groups;
  }, {});
};

/**
 * Calculate totals from array
 */
export const calculateTotals = (array, key) => {
  return array.reduce((sum, item) => sum + (parseFloat(item[key]) || 0), 0);
};

/**
 * Download file from blob
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  }
};

export default {
  formatDate,
  formatDateTime,
  formatCurrency,
  formatWeight,
  truncateText,
  getInitials,
  getStatusLabel,
  getShippingMethodLabel,
  isValidEmail,
  isValidPhone,
  generateId,
  debounce,
  sortByKey,
  filterBySearch,
  groupByKey,
  calculateTotals,
  downloadFile,
  copyToClipboard
};
